import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Users, Award, Building2, Globe, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { notifyFormSubmission } from "@/lib/notifyForm";
import { toast } from "sonner";

export default function TrainYourTeam() {
  const { language } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useSEO({
    title: "Train Your Team in AI & Prompt Engineering | QuantumBox AI for Business",
    description: "Upskill your entire workforce in AI, prompt engineering, RAG and automation. Bilingual EN/FR cohorts, sector-specific tracks, verifiable certifications. Get a custom quote.",
    keywords: "corporate AI training, team prompt engineering, enterprise AI upskilling, AI certification for teams, B2B AI training, QuantumBox Academy",
    canonical: "https://quantumbox.ai/train-your-team",
  });

  const [form, setForm] = useState({
    contact_name: "",
    work_email: "",
    phone: "",
    company: "",
    job_title: "",
    industry: "",
    country: "",
    team_size: "",
    training_goal: "",
    timeline: "",
    budget_range: "",
    preferred_format: "",
    notes: "",
  });

  const update = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.contact_name || !form.work_email || !form.company || !form.team_size) {
      toast.error("Please fill in the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("team_training_leads" as any).insert({
        ...form,
        language,
        source: "train-your-team-landing",
      });
      if (error) throw error;
      notifyFormSubmission("Train Your Team — B2B Lead", { ...form, language });
      setSubmitted(true);
      toast.success("Thanks! Our team will reach out within 1 business day.");
    } catch (err: any) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tiers = [
    {
      name: "Team Starter",
      price: "$99",
      unit: "/seat / year",
      seats: "5–25 seats",
      features: [
        "Full Academy access",
        "AI Fundamentals + Prompt Engineering certifications",
        "Admin dashboard & progress tracking",
        "Email support",
      ],
      highlight: false,
    },
    {
      name: "Team Pro",
      price: "$79",
      unit: "/seat / year",
      seats: "26–100 seats",
      features: [
        "Everything in Starter",
        "Advanced tracks: RAG, Agents, Automation",
        "Sector modules (Mining, Legal, Finance, Health)",
        "SSO + dedicated success manager",
        "Quarterly cohort kick-offs",
      ],
      highlight: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      unit: "engagement",
      seats: "100+ seats",
      features: [
        "Custom curriculum on your data & tools",
        "Instructor-led cohorts (EN/FR)",
        "Co-branded certifications",
        "On-site or hybrid delivery",
        "Optional consulting pull-through",
      ],
      highlight: false,
    },
  ];

  const tracks = [
    { icon: Sparkles, title: "Prompt Engineering Pro", desc: "Advanced techniques, chains, evals, guardrails." },
    { icon: Building2, title: "AI for Operations", desc: "Document AI, RPA-AI hybrids, internal copilots." },
    { icon: Globe, title: "Sector Tracks", desc: "Mining, Legal, Health, Finance, Public Sector." },
    { icon: Award, title: "Sovereign AI", desc: "On-prem LLMs, data residency, compliance-first prompting." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge variant="outline" className="mb-4">For Teams & Enterprises</Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              Make your entire team <span className="text-primary">fluent in AI</span> — in weeks, not years.
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              QuantumBox AI Academy already certified <strong>15,420+ professionals</strong>. Now bring it to your workforce with custom cohorts, sector tracks, and verifiable certifications.
            </p>
            <div className="flex flex-wrap gap-6 mb-8">
              <Stat value="15,420+" label="Professionals certified" />
              <Stat value="4.9/5" label="Average rating" />
              <Stat value="EN / FR" label="Bilingual delivery" />
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="#quote"><Button size="lg" variant="hero">Get a custom quote <ArrowRight className="ml-2 h-4 w-4" /></Button></a>
              <a href="#pricing"><Button size="lg" variant="outline">See pricing</Button></a>
            </div>
          </div>
          <Card className="shadow-elegant">
            <CardContent className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <Users className="h-6 w-6 text-primary" />
                <h3 className="font-semibold">Why companies choose QuantumBox</h3>
              </div>
              {[
                "Certifications recognized & verifiable on LinkedIn",
                "Bilingual (EN/FR) — ideal for global & francophone teams",
                "Sector-specific modules, not generic theory",
                "From e-learning to instructor-led & on-site",
                "Direct path to AI deployment with our consulting team",
              ].map((b) => (
                <div key={b} className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{b}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Tracks */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Tracks built for impact</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {tracks.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.title} className="hover:shadow-elegant transition-smooth">
                  <CardContent className="p-6">
                    <div className="p-3 bg-primary/10 rounded-lg w-fit mb-4"><Icon className="h-6 w-6 text-primary" /></div>
                    <h3 className="font-semibold mb-2">{t.title}</h3>
                    <p className="text-sm text-muted-foreground">{t.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 bg-gradient-subtle">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Simple, scalable pricing</h2>
            <p className="text-muted-foreground text-lg">Volume discounts kick in automatically. Annual billing.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {tiers.map((tier) => (
              <Card key={tier.name} className={tier.highlight ? "border-primary border-2 shadow-elegant relative" : ""}>
                {tier.highlight && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Most popular</Badge>
                )}
                <CardContent className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-bold">{tier.name}</h3>
                    <p className="text-sm text-muted-foreground">{tier.seats}</p>
                  </div>
                  <div>
                    <span className="text-4xl font-bold">{tier.price}</span>
                    <span className="text-muted-foreground"> {tier.unit}</span>
                  </div>
                  <ul className="space-y-2">
                    {tier.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="#quote" className="block">
                    <Button variant={tier.highlight ? "hero" : "outline"} className="w-full">
                      {tier.name === "Enterprise" ? "Talk to sales" : "Get a quote"}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section id="quote" className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Get a custom quote</h2>
            <p className="text-muted-foreground">Tell us about your team — we'll reply within 1 business day with a tailored proposal.</p>
          </div>

          {submitted ? (
            <Card className="border-primary">
              <CardContent className="p-8 text-center space-y-4">
                <CheckCircle className="h-12 w-12 text-primary mx-auto" />
                <h3 className="text-2xl font-bold">Request received</h3>
                <p className="text-muted-foreground">A QuantumBox specialist will contact you shortly with a proposal tailored to <strong>{form.company}</strong>.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 md:p-8">
                <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
                  <Field label="Full name *" required value={form.contact_name} onChange={(v) => update("contact_name", v)} />
                  <Field label="Work email *" type="email" required value={form.work_email} onChange={(v) => update("work_email", v)} />
                  <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} />
                  <Field label="Job title" value={form.job_title} onChange={(v) => update("job_title", v)} />
                  <Field label="Company *" required value={form.company} onChange={(v) => update("company", v)} />
                  <Field label="Industry" value={form.industry} onChange={(v) => update("industry", v)} placeholder="e.g. Mining, Finance, Health" />
                  <Field label="Country" value={form.country} onChange={(v) => update("country", v)} />

                  <SelectField
                    label="Team size *"
                    value={form.team_size}
                    onChange={(v) => update("team_size", v)}
                    options={["1–5", "6–25", "26–100", "101–500", "500+"]}
                  />
                  <SelectField
                    label="Timeline"
                    value={form.timeline}
                    onChange={(v) => update("timeline", v)}
                    options={["ASAP (this month)", "Next quarter", "6 months", "Exploring"]}
                  />
                  <SelectField
                    label="Budget range"
                    value={form.budget_range}
                    onChange={(v) => update("budget_range", v)}
                    options={["< $5k", "$5k–$25k", "$25k–$75k", "$75k+", "Not sure yet"]}
                  />
                  <SelectField
                    label="Preferred format"
                    value={form.preferred_format}
                    onChange={(v) => update("preferred_format", v)}
                    options={["Self-paced online", "Live online cohort", "On-site", "Hybrid"]}
                  />

                  <div className="md:col-span-2">
                    <Label>Training goal</Label>
                    <Textarea
                      rows={3}
                      placeholder="What outcomes do you expect for your team?"
                      value={form.training_goal}
                      onChange={(e) => update("training_goal", e.target.value)}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Additional notes</Label>
                    <Textarea
                      rows={2}
                      value={form.notes}
                      onChange={(e) => update("notes", e.target.value)}
                    />
                  </div>

                  <Button type="submit" variant="hero" size="lg" className="md:col-span-2" disabled={submitting}>
                    {submitting ? <><Loader2 className="animate-spin h-4 w-4 mr-2" /> Sending…</> : <>Request my custom quote <ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-2xl font-bold text-primary">{value}</div>
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
    </div>
  );
}

function Field({
  label, value, onChange, type = "text", required, placeholder,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} required={required} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function SelectField({
  label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
